import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';
import { useNetwork } from '../stores/network';
import { Button, Spinner } from './ui';
import { useT } from '../i18n';

export function LoadingState({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-ink-400" role="status">
      <Spinner className="size-8 text-volt" />
      <span className="text-sm">{label ?? t('Loading…')}</span>
    </div>
  );
}

export function EmptyState({ icon = '⬡', title, body, action }: { icon?: string; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <div className="mb-1 text-4xl text-ink-500">{icon}</div>
      <div className="font-display text-xl font-bold uppercase text-ink-200">{title}</div>
      {body ? <p className="max-w-xs text-sm text-ink-400">{body}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const online = useNetwork((s) => s.online);
  const t = useT();
  if (!online) return <OfflineState onRetry={onRetry} />;
  const message = error instanceof ApiError ? error.message : t('Something went wrong');
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center" role="alert">
      <div className="text-4xl">⚠️</div>
      <div className="font-display text-xl font-bold uppercase text-ink-200">{t('Couldn’t load this')}</div>
      <p className="max-w-xs text-sm text-ink-400">{message}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          {t('Try again')}
        </Button>
      ) : null}
    </div>
  );
}

export function OfflineState({ onRetry }: { onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center" role="status">
      <div className="text-4xl">📡</div>
      <div className="font-display text-xl font-bold uppercase text-ink-200">{t('You’re offline')}</div>
      <p className="max-w-xs text-sm text-ink-400">{t('This needs a connection. Your runs are still recorded and saved on this device.')}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          {t('Retry')}
        </Button>
      ) : null}
    </div>
  );
}

/** Renders the right state for a TanStack query result. */
export function QueryView<T>({
  query,
  children,
  empty,
  loadingLabel,
}: {
  query: { data: T | undefined; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  children: (data: T) => ReactNode;
  empty?: (data: T) => ReactNode | null;
  loadingLabel?: string;
}) {
  if (query.isPending) return <LoadingState label={loadingLabel} />;
  if (query.isError && query.data === undefined) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  const data = query.data as T;
  const e = empty?.(data);
  if (e) return <>{e}</>;
  return <>{children(data)}</>;
}
